/**
 * useMarketplaceConnectionAttentionCount — live count for the marketplace
 * sidebar's "Connections" badge: connections that are held or still charging,
 * i.e. exactly the Connections ledger's "Needs attention" tab
 * (`connectionNeedsAttention`, the one shared predicate).
 *
 * A thin subscription to `stores/marketplaceConnectionAttention.ts`: the page
 * sidebar and the phone-width strip share one `GET /api/marketplace/pro/connections`
 * and one count. A failed fetch degrades to 0 (no badge). Refetches on the
 * shared marketplace-requests bump, since a confirm creates a charging
 * connection. `enabled=false` issues no request.
 */
import { useEffect } from "react";
import { IS_PREVIEW } from "../lib/pro-mode";
import { useMarketplaceConnectionAttentionStore } from "../stores/marketplaceConnectionAttention";
import { useProToken } from "./useProToken";

/** Preview fixtures carry one held connection (see `MarketplaceConnectionsPage`). */
const PREVIEW_COUNT = 1;

export function useMarketplaceConnectionAttentionCount(enabled = true): number {
  const getToken = useProToken();
  useEffect(() => {
    if (IS_PREVIEW || !enabled) return;
    return useMarketplaceConnectionAttentionStore.getState().attach({ getToken });
  }, [getToken, enabled]);
  const count = useMarketplaceConnectionAttentionStore((s) => s.count);

  if (IS_PREVIEW) return enabled ? PREVIEW_COUNT : 0;
  return enabled ? count : 0;
}
